import {
  Stack,
  Avatar,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FileText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";


import type { TopicCardProps } from "./TopicCardPost.types";

import {
  Icon,
  CtaCommunityOverlay,
  ModifyContentModal,
  CommentsSection
  ,Link } from "..";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { DeleteContentModal } from "../../components/DeleteContentModal";
import {
  ActionLink,
  BodyTypography,
  LinkTypography,
  OwnerNameTypography,
  TopicCardBody,
  TopicCardTop
} from "../../components/TopicCard/TopicCard.styles";
import { TopicCardSignature } from "../../components/TopicCardSignature";
import { POST_TYPE, ROLES } from "../../consts";
import { useAuthStore } from "../../stores";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import { stringAvatar, useGetCreatedLabel } from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

export function TopicCardPost({
  attachments,
  body,
  changed,
  commentsNumber,
  created,
  id,
  likes,
  ownerImage,
  ownerName,
  topicOwner,
  selectedFilter,
  selectedTopic
}: TopicCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isModifyOpen, setIsModifyOpen] = useState(false);
  const[showComments,setShowComments]=useState(false);
  const userData = useAuthStore(state => state.userData);

  const isModerator = userData?.roles.some(role => role === ROLES.BUSINESS_ADMIN || role === ROLES.PRODUCT_OWNER) ;
  const userId = useAuthStore(state => state.userData?.id_user);
  const isEditable =  (userId && topicOwner == userId.toString());

  const isModified = created !== changed;

  return (
    <CommonWidgetContainer
      sx={ {
        width:"100% !important",
        maxWidth:"100%" ,
        "& p": {
          color: theme.customColors.exploreCardText
        },
        "& span": {
          color: theme.customColors.exploreCardText
        },
        "& .icon path": {
          fill: `${theme.customColors.exploreCardText} !important`
        }
      } }
      hasSidePadding={ true }
      hoverBorderAccent={ false }
    >
      <TopicCardTop
        direction={ "row" }
        gap={ "16px" }
        alignItems={ "center" }
      >
        <Avatar
          alt={ `${t("avatar_user")} ${ownerName}` }
          { ...stringAvatar(ownerName) }
          src={ ownerImage }
          sx={ {
            height: isMobile ? "32px" : "56px",
            width: isMobile ? "32px" : "56px"
          } }
        />
        <Stack
          gap={ 1 }
          flexGrow={ 1 }
        >
          <Stack
            direction={ "row" }
            justifyContent={ "space-between" }
            alignItems={ "center" }>
            <OwnerNameTypography>{ ownerName }</OwnerNameTypography>
            <BadgeBox
              background={ theme.customColors.backgroundSecondary }
              textColor={ "inherit" }
              border={ `1px solid ${theme.customColors.textTag}` }>
              <Stack
                direction={ "row" }
                alignItems={ "center" }
                gap={ "8px" }
              >
                <FileText size={ 16 } color={ theme.customColors.textTag }/>
                <TypographyTextTag>
                  { t("post") }
                </TypographyTextTag>
              </Stack>
            </BadgeBox>
          </Stack>

          <Stack
            direction={ "row" }
            justifyContent={ "space-between" }
            alignItems={ "center" }
            flexWrap={ "wrap" }>

            <TopicCardSignature
              created={  useGetCreatedLabel(created)  }
              isModified={ isModified }
              size={ "medium" }
            />

            {
              isEditable || isModerator ?
                <Stack
                  direction="row"
                  alignItems={ "flex-end" }
                  gap={ "1rem" }
                >
                  {
                    isEditable ?
                      <ActionLink
                        onClick={ () => setIsModifyOpen(true) }
                        sx={ { color:theme.customColors.accentVariantA } }
                      >
                        { t("modify") }
                      </ActionLink>  : null
                  }

                  <ActionLink
                    onClick={ () => setIsDeleteOpen(true) }
                    sx={ { color:theme.customColors.accentVariantA } }
                  >
                    { t("delete") }
                  </ActionLink>
                </Stack>  :
                null
            }
          </Stack>
        </Stack>
      </TopicCardTop>

      <TopicCardBody>
        <BodyTypography>
          { body }

        </BodyTypography>
        {
          attachments?.["link"]?.[0]?.content  ?

            <Link
              href={ attachments?.["link"]?.[0]?.content }
              target={ "_blank" }
              rel="noreferrer"
              paddingBottom={ isMobile ? "16px" : "0" }
            >

              <Stack direction={ "row" } gap={ "8px" }>
                <LinkTypography>
                  {
                    attachments?.["link"]?.[0]?.name ||
                    getUrlname(attachments?.["link"]?.[0]?.content )
                  }
                </LinkTypography>
                <Icon
                  icon={ "Icons_link" }
                  width={ "20px" }
                  height={ "20px" }
                  color={ theme.customColors.backgroundPrimaryCta }
                />
              </Stack>
            </Link>

            :
            null
        }

      </TopicCardBody>

      <CtaCommunityOverlay
        id={ id }
        comments={ commentsNumber }
        likes={ likes }
        commentsOpened={ showComments }
        commentsClickAction={ () => {
          setShowComments(!showComments);
        } }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
      />


      <CommentsSection
        topicOwnerId={ topicOwner }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
        discussionId={ id }
        className={ showComments ? "visible" : "" }
      />

      {
        isDeleteOpen ?

          <DeleteContentModal
            selectedFilter={ selectedFilter }
            selectedTopic={ selectedTopic }
            id={ id }
            isModerator={ Boolean(isModerator) }
            postType={ POST_TYPE.FREE }
            onClose={ ()=> {
              setIsDeleteOpen(false);
            } }
            closeDrawer={ ()=> {
              setIsDeleteOpen(false);
            } }
            open={ isDeleteOpen }
          /> : null
      }

      {
        isModifyOpen ?
          <ModifyContentModal
            selectedFilter={ selectedFilter }
            selectedTopic={ selectedTopic }
            open={ isModifyOpen }
            onClose={ () => setIsModifyOpen(false) }
            id={ id }
            postType={ POST_TYPE.FREE }
            linklabel={ attachments?.["link"]?.[0]?.name ?? "" }
            linkvalue={ attachments?.["link"]?.[0]?.content ?? "" }
            body={ body }
            closeDrawer={ () => setIsModifyOpen(false) }
          /> : null

      }



    </CommonWidgetContainer>
  );
  function getUrlname(url: string) {

    return url.replace(/^https?:\/\//, "");
  }


}
